import { useCallback } from 'react';
import {
  type ApplyRewardFailureCode,
  type ValidateRewardFailureCode,
} from '@sg/graphql-schema';

import {
  type ApplyRewardErrorCode,
  type ApplyRewardErrorMessages,
} from '../useApplyRewardErrorMessage.constants';
import {
  useFallbackApplyRewardErrorMessagesByCode,
  useFallbackApplyRewardErrorMessagesByReason,
  useFallbackApplyRewardGenericErrorMessages,
} from './useApplyRewardErrorMessageFallbacks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches reward error messages from Contentful (with intl fallbacks).
 *
 * @see {@link https://app.contentful.com/spaces/wme4s8lvzccr/entries/12qSE321S24oul9rg2VXnx}
 */
export const useApplyRewardErrorMessages = (params: Params) => {
  const { messages } = params;

  // ─── Fallbacks ───────────────────────────────────────────────────────

  const fallbackGenericMessages = useFallbackApplyRewardGenericErrorMessages();
  const fallbackMessagesByCode = useFallbackApplyRewardErrorMessagesByCode();
  const fallbackMessagesByReason = useFallbackApplyRewardErrorMessagesByReason(); // prettier-ignore

  const defaultErrorMessage =
    messages?.REWARD_NOT_APPLICABLE ??
    fallbackMessagesByCode.REWARD_NOT_APPLICABLE;
  const networkErrorMessage =
    messages?.NETWORK_ERROR ?? fallbackGenericMessages.NETWORK_ERROR;
  const multipleErrorsMessage =
    messages?.REWARD_MULTIPLE_ERRORS ??
    fallbackGenericMessages.REWARD_MULTIPLE_ERRORS;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getApplyRewardFailureCode = useCallback(
    (code: ApplyRewardFailureCode) => {
      const messageFromContentful = messages?.[code as ApplyRewardErrorCode];
      const fallbackMessage = fallbackMessagesByCode[code];

      return messageFromContentful ?? fallbackMessage ?? defaultErrorMessage;
    },
    [defaultErrorMessage, fallbackMessagesByCode, messages],
  );

  const getApplyRewardFailureReason = useCallback(
    (reason: ValidateRewardFailureCode) => {
      const messageFromContentful = messages?.[reason as ApplyRewardErrorCode];
      const fallbackMessage = fallbackMessagesByReason[reason];

      return messageFromContentful ?? fallbackMessage ?? defaultErrorMessage;
    },
    [defaultErrorMessage, fallbackMessagesByReason, messages],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    defaultErrorMessage,
    networkErrorMessage,
    multipleErrorsMessage,
    getApplyRewardFailureCode,
    getApplyRewardFailureReason,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  messages: ApplyRewardErrorMessages | undefined;
};
